import { useState, useEffect } from "react";
import { NavLink, Link, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaBell, FaChevronDown, FaBars, FaXmark } from "react-icons/fa6";
import {
  HiOutlineHome,
  HiOutlineMap,
  HiOutlineShieldCheck,
  HiOutlineDocumentText,
  HiOutlineBookOpen,
  HiOutlineHeart,
  HiOutlineUser,
  HiOutlinePhone,
  HiOutlineBell,
  HiOutlineAcademicCap,
  HiOutlineClipboardDocumentCheck,
  HiOutlinePlusCircle,
  HiOutlineSparkles,
  HiOutlineChatBubbleLeftRight,
  HiOutlineLockClosed,
  HiOutlineTicket,
  HiOutlineArrowRight,
  HiOutlineInboxStack,
} from "react-icons/hi2";
import { Toaster } from "react-hot-toast";
import ForgeLogo from "../components/logo";
import CircularBadge from "../components/badge";
import { getPortalToken, clearPortalToken, portalAPI } from "./portalApi";
import "./PortalLayout.css";

/*
 * Navigation — grouped, every item has a word AND an icon.
 * `area` matches the access map from GET /api/portal/me, so an item that
 * isn't open yet shows a lock and, when clicked, a page explaining exactly
 * what unlocks it (never a dead end or a blank error).
 */
const NAV_GROUPS = [
  {
    label: "Your journey",
    items: [
      { to: "/portal/dashboard", area: "dashboard", Icon: HiOutlineHome, label: "Home" },
      { to: "/portal/journey", area: "journey", Icon: HiOutlineMap, label: "My Journey" },
      { to: "/portal/book", area: "book", Icon: HiOutlineTicket, label: "Book a Retreat" },
      { to: "/portal/my-retreat", area: "my-retreat", Icon: HiOutlineSparkles, label: "My Retreat" },
    ],
  },
  {
    label: "Application",
    items: [
      { to: "/portal/notifications", area: "notifications", Icon: HiOutlineBell, label: "Messages", badge: true },
      { to: "/portal/verification", area: "verification", Icon: HiOutlineShieldCheck, label: "Verification" },
      { to: "/portal/prescreening", area: "prescreening", Icon: HiOutlineClipboardDocumentCheck, label: "Prescreening" },
      { to: "/portal/medical", area: "medical", Icon: HiOutlinePlusCircle, label: "Medical Clearance" },
      { to: "/portal/documents", area: "documents", Icon: HiOutlineDocumentText, label: "Documents" },
    ],
  },
  {
    label: "Community & learning",
    items: [
      { to: "/portal/community", area: "community", Icon: HiOutlineChatBubbleLeftRight, label: "Community" },
      { to: "/portal/courses", area: "courses", Icon: HiOutlineAcademicCap, label: "Courses" },
      { to: "/portal/resources", area: "resources", Icon: HiOutlineBookOpen, label: "Resources" },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/portal/donations", area: "donations", Icon: HiOutlineHeart, label: "Donations" },
      { to: "/portal/profile", area: "profile", Icon: HiOutlineUser, label: "Profile" },
    ],
  },
];

const MOBILE_NAV = [
  { to: "/portal/dashboard", area: "dashboard", Icon: HiOutlineHome, label: "Home" },
  { to: "/portal/journey", area: "journey", Icon: HiOutlineMap, label: "Journey" },
  { to: "/portal/book", area: "book", Icon: HiOutlineTicket, label: "Book" },
  { to: "/portal/community", area: "community", Icon: HiOutlineChatBubbleLeftRight, label: "Community" },
  { to: "/portal/notifications", area: "notifications", Icon: HiOutlineInboxStack, label: "Messages" },
];

/** /portal/<segment>/… → access-map key */
const areaForPath = (pathname) => {
  const seg = (pathname.split("/")[2] || "dashboard").toLowerCase();
  return seg === "apply" ? "book" : seg;
};

/** Shown in place of a page that isn't open for this member yet. */
function LockedPanel({ reason, member }) {
  const needsApplication = member && !member.hasApplication;
  return (
    <div className="pc pl-locked">
      <div className="pl-locked__icon" aria-hidden="true"><HiOutlineLockClosed /></div>
      <p className="pl-locked__eyebrow">Not open yet</p>
      <h2 className="pl-locked__title">This part of your portal opens a little later</h2>
      <p className="pl-locked__reason">{reason}</p>
      <div className="pl-locked__actions">
        {needsApplication && (
          <Link to="/apply" className="p-btn p-btn--rust">Start your application <HiOutlineArrowRight /></Link>
        )}
        <Link to="/portal/dashboard" className="p-btn p-btn--outline">See your next steps</Link>
      </div>
    </div>
  );
}

export default function PortalLayout({ children, pageTitle, eyebrow, quote, quoteAuthor }) {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [member, setMember] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Auth guard + fetch member info
  useEffect(() => {
    if (!getPortalToken()) {
      const back = `${location.pathname.replace(/^\/portal\//, "")}${location.search}`;
      navigate(`/portal/login?redirect=${encodeURIComponent(back)}`, { replace: true });
      return;
    }
    portalAPI
      .me()
      .then((res) => setMember(res?.data || null))
      .catch(() => {
        clearPortalToken();
        navigate("/portal/login", { replace: true });
      });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSignOut = () => {
    clearPortalToken();
    navigate("/portal/login");
    setSidebarOpen(false);
  };
  const close = () => setSidebarOpen(false);
  const [unread, setUnread] = useState(0);

  // Unanswered team messages — drives the bell + "Messages" badge
  useEffect(() => {
    if (!getPortalToken()) return;
    portalAPI
      .tasks()
      .then((res) => setUnread(res?.data?.unansweredMessages || 0))
      .catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const locks = member?.access?.locks || {};
  const area = areaForPath(location.pathname);
  const lockReason = member ? locks[area] || null : null;

  return (
    <div className="pl">
      {/* Toasts for every portal page (confirmations, errors) */}
      <Toaster position="top-center" toastOptions={{ duration: 4000, style: { fontSize: "0.88rem", borderRadius: "8px", background: "#1e2a1e", color: "#fff" } }} />
      {/* ── Desktop Sidebar ── */}
      <aside className={`pl__sidebar ${sidebarOpen ? "pl__sidebar--open" : ""}`}>
        <Link to="/portal/dashboard" className="pl__logo" onClick={close}>
          <ForgeLogo fill="var(--portal-rust)" width={36} height={36} />
          <div className="pl__logo-words">
            <span className="pl__logo-forge">FORGE</span>
            <span className="pl__logo-sub">RETREAT</span>
            <span className="pl__logo-sub">COLOMBIA</span>
          </div>
        </Link>

        <nav className="pl__nav" aria-label="Portal navigation">
          {NAV_GROUPS.map((group) => (
            <div key={group.label} className="pl__nav-group">
              <p className="pl__nav-group-label">{group.label}</p>
              {group.items.map(({ to, area: itemArea, Icon, label, badge }) => {
                const locked = !!(member && locks[itemArea]);
                return (
                  <NavLink
                    key={to}
                    to={to}
                    title={locked ? locks[itemArea] : undefined}
                    className={({ isActive }) => `pl__nav-link ${isActive ? "pl__nav-link--active" : ""} ${locked ? "pl__nav-link--locked" : ""}`}
                    onClick={close}
                  >
                    <Icon className="pl__nav-icon" aria-hidden="true" />
                    <span className="pl__nav-text">{label}</span>
                    {locked && <HiOutlineLockClosed className="pl__nav-lock" aria-label="Not open yet" />}
                    {!locked && badge && unread > 0 && <span className="pl__nav-badge">{unread}</span>}
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>

        <div className="pl__help">
          <div className="pl__help-icon">
            <HiOutlinePhone />
          </div>
          <div>
            <p className="pl__help-title">Need Help?</p>
            <p className="pl__help-sub">We're here for you.</p>
            <a href="mailto:support@forge.ngo" className="pl__help-link">
              support@forge.ngo
            </a>
          </div>
        </div>

        <div className="pl__sidebar-badge">
          <CircularBadge text="ONE JOURNEY · SAME PATH FORWARD ·" colors="rgba(255,255,255,0.35)" logoSize={20} />
        </div>
      </aside>

      {sidebarOpen && <div className="pl__sidebar-backdrop" onClick={close} aria-hidden="true" />}

      {/* ── Main ── */}
      <div className="pl__main">
        {/* ── Topbar — desktop shows bell + avatar name; mobile shows hamburger | logo | bell ── */}
        <header className="pl__topbar">
          {/* Mobile hamburger */}
          <button type="button" className="pl__topbar-menu" onClick={() => setSidebarOpen((v) => !v)} aria-label="Toggle menu">
            {sidebarOpen ? <FaXmark /> : <FaBars />}
          </button>

          {/* Mobile centre logo */}
          <Link to="/portal/dashboard" className="pl__topbar-logo">
            <ForgeLogo fill="var(--portal-rust)" width={30} height={30} />
            {/* Sizes live in PortalLayout.css with the colours, rather than
                half here and half there. (UX-REVIEW LOG-007) */}
            <div className="pl__logo-words">
              <span className="pl__logo-forge">FORGE</span>
              <span className="pl__logo-sub">RETREAT COLOMBIA</span>
            </div>
          </Link>

          {/* Right controls — bell + avatar */}
          <div className="pl__topbar-right">
            <Link to="/portal/notifications" className="pl__topbar-bell" aria-label={`${unread > 0 ? unread : ""} notifications`}>
              <FaBell />
              {unread > 0 && <span className="pl__bell-badge">{unread > 0 ? unread : ""}</span>}
            </Link>

            {/* Avatar with dropdown */}
            <div className="pl__topbar-user">
              <button type="button" className="pl__avatar-btn" onClick={() => setProfileOpen((v) => !v)} aria-expanded={profileOpen} aria-label="Your account menu">
                {/* Circular avatar */}
                <div className="pl__avatar" aria-hidden="true">
                  {(member?.firstName || "…")[0]}
                  {member?.lastName?.[0] || ""}
                </div>
                <FaChevronDown className="pl__avatar-chevron" style={{ transform: profileOpen ? "rotate(180deg)" : "rotate(0)" }} />
              </button>

              <AnimatePresence>
                {profileOpen && (
                  <motion.div className="pl__profile-dropdown" initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.2 }}>
                    <div className="pl__profile-top">
                      <div className="pl__avatar pl__avatar--lg">
                        {(member?.firstName || "…")[0]}
                        {member?.lastName?.[0] || ""}
                      </div>
                      <div>
                        <p className="pl__profile-name">
                          {member?.firstName || "…"} {member?.lastName || ""}
                        </p>
                        <p className="pl__profile-email">{member?.email || ""}</p>
                      </div>
                    </div>
                    <Link to="/portal/profile" className="pl__profile-link" onClick={() => setProfileOpen(false)}>
                      My Profile
                    </Link>
                    <Link to="/portal/notifications" className="pl__profile-link" onClick={() => setProfileOpen(false)}>
                      Messages
                    </Link>
                    <Link to="/portal/dashboard" className="pl__profile-link" onClick={() => setProfileOpen(false)}>
                      My next steps
                    </Link>
                    <div className="pl__profile-divider" />
                    <button type="button" className="pl__profile-signout" onClick={handleSignOut}>
                      Sign Out
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {/* ── Page content ── */}
        <main className="pl__content">
          {/* Page header */}
          <div className="pl__page-header">
            <div className="pl__page-header-left">
              {eyebrow && <p className="pl__eyebrow">{eyebrow}</p>}
              <h1 className="pl__page-title">{pageTitle}</h1>
            </div>
            {quote && (
              <div className="pl__page-quote">
                <blockquote className="pl__quote-text">"{quote}"</blockquote>
                {quoteAuthor && <cite className="pl__quote-author">— {quoteAuthor}</cite>}
              </div>
            )}
            <div className="pl__page-mtn" aria-hidden="true">
              <img src="./assets/mountainsForst.png" alt="" />
            </div>
          </div>
          {lockReason ? <LockedPanel reason={lockReason} member={member} /> : children}
        </main>
      </div>

      {/* ── Mobile bottom nav ── */}
      <nav className="pl__mobile-nav" aria-label="Mobile navigation">
        {MOBILE_NAV.map(({ to, area: itemArea, Icon, label }) => (
          <NavLink key={to} to={to} className={({ isActive }) => `pl__mobile-nav-item ${isActive ? "pl__mobile-nav-item--active" : ""} ${member && locks[itemArea] ? "pl__mobile-nav-item--locked" : ""}`}>
            <span className="pl__mobile-nav-icon-wrap">
              <Icon className="pl__mobile-nav-icon" />
              {itemArea === "notifications" && unread > 0 && <span className="pl__mobile-dot" aria-hidden="true" />}
            </span>
            <span className="pl__mobile-nav-label">{label}</span>
          </NavLink>
        ))}
        {/* "More" used to sit here and opened the same drawer as the
            hamburger in the topbar — two controls, one destination, and it
            took a sixth of the width that the real destinations needed.
            The hamburger is the drawer's single entry point.
            (UX-REVIEW LOG-010, which is what makes room for LOG-009) */}
      </nav>
    </div>
  );
}
